import { toast } from "react-toastify";
import { validateRequiredFields } from "../../utils/commonComponent/errorAlert";
import { fetchQuery } from "../../utils/commonComponent/fetched";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL, MAX_COLS } from "../../global";
import moment from "moment";
import { getCustomColumnCount } from "./helper";
import { processDatasheet } from "./ObservedReadings/helper";
import { notifiy } from "../notification/Notification";

export const updateCertificateAfterObservation = async (datasheetDetails) => {
  try {
    const certificateUrl = BASE_URL + `certificate/${datasheetDetails?.observationSheet_certificateId}`;
    const certPayload = {
      reviewedBy: null,
      reviewedDate: null,
      preparedBy: null,
      preparedDate: null,
      authorizedBy: null,
      authorizedDate: null,
      rejectedBy: null,
    };
    await axiosWithToken.patch(certificateUrl, certPayload);
  } catch (error) {
    console.error("Error updating Certificate:", error);
  }
};

export const addUnitSuffix = (val, unit) => {
  if (String(val).includes("_unit_") || String(val).includes("_unit_"))
    return val;
  return `${String(val)}#${
    String(unit).includes("_unit_") ? unit.split("_unit_")[1] : unit
  }`;
};

export const handleSubmit = async (
  params,
  datasheetDetails,
  staticReadingRows,
  staticTables,
  isThisSave = false,
  selectedStandardIds,
  fileAttachment,
  witnessChanges
) => {
  //  1. Validate form fields
  const fieldsToValidate = [
    {
      value: datasheetDetails?.observationSheet_startTestingDate,
      label: "Start Testing Date is required.",
    },
    // {
    //   value: datasheetDetails?.observationSheet_endTestingDate,
    //   label: "End Testing Date is required.",
    // },
  ];

  if (!validateRequiredFields(fieldsToValidate)) return;

  try {
    //  2. Sort and clean rows
    const staticTablesIds = staticTables.map((t) => t.id);
    staticReadingRows.sort((a, b) => a.tableId - b.tableId);



    const processedRows = (() => {
      if (!staticReadingRows?.length) return [];

      // 1️⃣ Collect all unique keys from all rows (after cleaning)
      const allKeys = new Set();
      staticReadingRows.forEach((row) => {
        Object.keys(row).forEach((k) => allKeys.add(k));
      });

      // 2️⃣ Convert to array for easier iteration
      const uniqueKeys = [...allKeys];

      let currentRowIndex = -1;

      // 3️⃣ Build processed rows dynamically
      return staticReadingRows.map((row, idx, arr) => {
        const cleanRow = {};

        // Copy only allowed keys dynamically
        uniqueKeys.forEach((key) => {
          if (key !== "id") {
            cleanRow[key] = row[key] ?? null;
          }
        });

        // Replace/normalize certain fields
        cleanRow.observationsheetId = row.datasheetId || params.id;
        delete cleanRow.datasheetId;

        // Ensure uncertainty & standardRanges exist
        if (!("uncertainty" in cleanRow)) cleanRow.uncertainty = null;
        if (!("standardRanges" in cleanRow)) cleanRow.standardRanges = null;

        // Handle column suffix logic
        const customColumnCount = getCustomColumnCount(
          row.tableId,
          null,
          staticTables,
          "static"
        );

        if (String(row.c1).includes("_unit_")) currentRowIndex = idx;

        if (!String(row.c1).includes("_rh_")) {
          for (let j = 0; j < customColumnCount; j++) {
            const colKey = `c${j + 1}`;
            cleanRow[colKey] = addUnitSuffix(
              row[colKey] ?? null,
              currentRowIndex > -1 ? arr[currentRowIndex][colKey] : ""
            );
          }
        }

        // Pad missing c-columns dynamically (based on MAX_COLS)
        for (let j = 1; j <= MAX_COLS; j++) {
          const colKey = `c${j}`;
          if (!(colKey in cleanRow)) cleanRow[colKey] = null;
        }

        return cleanRow;
      });
    })();

    //  3. Prepare Observation Sheet payload
    const payload = {
      startTestingDate: datasheetDetails?.observationSheet_startTestingDate ? moment(datasheetDetails?.observationSheet_startTestingDate).format("YYYY-MM-DD") : null,
      endTestingDate: datasheetDetails?.observationSheet_endTestingDate ? moment(datasheetDetails?.observationSheet_endTestingDate).format("YYYY-MM-DD") : null,
      testedBy: datasheetDetails?.observationSheet_testedBy || localStorage.getItem("id"),
      designation: localStorage.getItem("designation"),
      configuration: JSON.stringify(datasheetDetails?.observationSheet_configuration) || null,
      totalReadings: processedRows?.length || 0,
      masterUsedId: selectedStandardIds,
      fileAttachment: fileAttachment?.join(",") || null,
      additionalTestDetails: JSON.stringify(datasheetDetails?.observationSheet_additionalTestDetails || {}) || null,
    };

    //  4. Update Observation Sheet
    await axiosWithToken.patch(`${BASE_URL}observationSheet/${params.id}`, payload);

    //  5. Delete old readings
    const deleteQuery = {
      query: `DELETE FROM observationSheetReadings WHERE observationSheetId = ${params.id} AND tableId IN (${staticTablesIds.join(",")});`,
    };
    await axiosWithToken.post(`${BASE_URL}dynamic`, deleteQuery);

    //  6. Insert new readings
    await axiosWithToken.post(
      `${BASE_URL}observationSheetReadings/bulk`,
      processedRows
    );

    //  7. Update certificate
    await updateCertificateAfterObservation(datasheetDetails);
    
    //  8. Update witness
    if (isThisSave) {
      await handleWithnessUpdate(
        params.id,
        datasheetDetails?.observationSheet_certificateId,
        witnessChanges
      );
    }

    toast.success("Observation Sheet Updated!");
    if (isThisSave) window.location.reload();
  } catch (error) {
    console.error("❌ Error updating Observation Sheet:", error);
    toast.error("Something went wrong while updating the Observation Sheet.");
  }
};

export const observationSheetApprove = async (datasheetId) => {
  const user = localStorage.getItem("id");
  let ids = Array.isArray(datasheetId) ? datasheetId : [datasheetId];
  try {
    const url = `${BASE_URL}dynamic`;
    const query = `SELECT now() as date`;
    const response = await axiosWithToken.post(url, { query });
    const approvedDate = new Date(response.data[0]?.date || null);
    const formattedDate = moment(approvedDate).format("YYYY-MM-DD HH:mm:ss");

    axiosWithToken
      .post(BASE_URL + `dynamic`, {
        query: `UPDATE observationSheet SET approvedBy = ${user}, approvedByDate = '${formattedDate}' WHERE id IN (${ids.join(
          ","
        )})`,
      })
      .then((res) => {
        toast.success("observationSheet Approved successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 500);
      })
      .catch((err) => console.log(err));
  } catch (error) {
    console.error("Error in approving observationSheet:", error);
  }
};

export const processDatasheetReadings = async (
  datasheetContextValue,
  _staticReadingRows = null
) => {
  // 0. extract the context

  const {
    state: {
      staticTables,
      staticReadingRows,
      certificateStaticTables,
      datasheetDetails,
      standardRanges,
      selectedStandardMasters,
      referenceData,
      settings,
      currentOperationState,
      supportiveStandardMasterArray,
    },
  } = datasheetContextValue;
  // disable all alerts
  let originalAlert = window.alert;
  let originalConfirm = window.confirm;
  if (currentOperationState == 1) {
    originalAlert = window.alert;
    originalConfirm = window.confirm;
    window.alert = () => {};
    window.confirm = () => true;
  }

  // 1. process data before submit
  let latestStaticReadingRows = _staticReadingRows
    ? _staticReadingRows
    : staticReadingRows;
  let updatedStaticReadingRows = await processDatasheet(
    staticTables,
    latestStaticReadingRows,
    settings?.["datasheetObservedReadingFallbackValue"],
    certificateStaticTables,
    datasheetDetails,
    standardRanges,
    selectedStandardMasters,
    referenceData,
    supportiveStandardMasterArray
  );
  if (!updatedStaticReadingRows) {
    return null;
  }

  // reenable original alerts
  if (currentOperationState == 1) {
    window.alert = originalAlert;
    window.confirm = originalConfirm;
  }

  return updatedStaticReadingRows;
};

// send for approval
export const sendForApproval = async (ids, row) => {
  let nextActiveRole = [];
  try {
    const userResponse = await axiosWithToken.post(BASE_URL + `dynamic`, {
      query: `SELECT id, userName FROM userMaster WHERE status = 1 AND FIND_IN_SET(${row?.inward_disciplineId}, disciplineId) AND roles REGEXP '(^|,)(2|3|4)(,|$)'`
    });
    nextActiveRole = userResponse?.data || [];
  } catch (err) {
    console.log(err);
    nextActiveRole = [];
  }
  if (row?.observationSheet_totalReadings == null || row?.observationSheet_totalReadings == 0) {
    alert("Testing is not completed.");
    return;
  }
  const confirmed = window.confirm("Are you sure you want to send for approval?");
  if (!confirmed) return;
  const normalizedIds = Array.isArray(ids) ? ids : [ids];
  axiosWithToken
    .post(BASE_URL + `dynamic`, {
      query: `UPDATE observationSheet SET requestApproval = 1 WHERE id IN (${normalizedIds.join(",")})`,
    })
    .then((res) => {
      toast.success("Datasheet sent for approval successfully!");
      if(nextActiveRole.length > 0){
        // Loop through all users in nextActiveRole and send notifications
         nextActiveRole.forEach(user => {
          const notification = {
            _from: localStorage.getItem("id"),
            _to: user?.id,
            body: `${row?.materialTestings_testName} (${row?.jobNumber})\n A certificate is ready for your review. Kindly proceed with the approval..`,
            type: 1,
          };
  
          // Call the notifiy function for each user
          notifiy(notification, () => {
            console.log(`Notified of certificate for approval`);
          });
        });
      }
      setTimeout(() => {
        window.location.reload();
      }, 500);
    })
    .catch((err) => console.log(err));
};

// witness history delete
export const handleWithnessUpdate = async (observationSheetId,certificateId, { newRows, deletedIds, editedRows } ) => {
  try {
    // 1. Delete removed rows
    if (deletedIds.length > 0) {
      await fetchQuery(`DELETE FROM ObservationWitnessHistory WHERE id IN (${deletedIds.join(",")})`
      );
    }

    // 2. PATCH edited existing rows
    for (const [id, data] of Object.entries(editedRows || {})) {
      await axiosWithToken.patch(BASE_URL + `ObservationWitnessHistory/${id}`, {
        name:        data.name,
        designation: data.designation,
        companyName: data.companyName,
        Sign:        data.Sign,
      });
    }

    // 3. Insert new rows
    if (newRows.length > 0) {
      const bulkPayload = newRows.map((row) => ({
        observationSheetId,
        certificateId,
        name: row.name,
        designation: row.designation,
        companyName: row.companyName,
        Sign: row.Sign,
      }));
      await axiosWithToken.post(BASE_URL +`ObservationWitnessHistory/bulk`, bulkPayload);
    }
  } catch (error) {
    console.error("Error committing witness changes:", error);
    throw error;
  }
};